import {
  CalendarDaysIcon,
  QrCodeIcon,
  UserCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/solid';
import { formatUrl } from 'utils/shared/formatUrl';
import Calendar from 'svg/Calendar';
import Court from 'svg/Court';
import Home from 'svg/Home';
import Players from 'svg/Players';
import PlusWithoutBG from 'svg/PlusWithoutBG';
import RacketBall from 'svg/RacketBall';
import RacketTwo from 'svg/RacketTwo';
import Trophy from 'svg/Trophy';
import UserCircle from 'svg/UserCircle';
import { FindTab } from 'screens/Find/types';
import type { ProgrammingTypeValue } from 'screens/ProgramEditor/types';
import NotificationIndicator from 'components/NotificationIndicator';

export const ROOT_PAGE = '/';
export const HOME_PAGE_REWRITE = '/home/rewrite/';
export const TOURNAMENT_ORGANIZER_DASHBOARD = '/tournaments-dashboard';
export const LANDING_PAGE = '/tour';
export const TOURNAMENT_MARKET_PLACE = '/tournaments';
export const PLAY_PAGE = '/play';
export const HOME_PAGE = ROOT_PAGE;
export const TEMP_COACH_HOME = '/home';
export const TRAIN_PAGE = '/train';
export const EXPLORE_PAGE = '/explore';
export const ABOUT_PAGE = '/about';
export const CONTACT_PAGE = '/contact';
export const SUPPORT_PAGE = '/support';
export const BLOG_PAGE = '/blog';
export const GUIDES_PAGE = '/guides';
export const NYC_TENNIS_GUIDE_PAGE = `${GUIDES_PAGE}/nyc-tennis`;
export const NYC_TENNIS_GUIDE_PARKS_PAGE = `${NYC_TENNIS_GUIDE_PAGE}/parks`;
export const NYC_TENNIS_GUIDE_PERMITS_PAGE = `${NYC_TENNIS_GUIDE_PAGE}/permits`;
export const NYC_TENNIS_GUIDE_RESERVATIONS_PAGE = `${NYC_TENNIS_GUIDE_PAGE}/reservations`;
export const NYC_TENNIS_GUIDE_WALK_UP_PAGE = `${NYC_TENNIS_GUIDE_PAGE}/walk-up`;
export const getNycTennisGuideParksUrl = (params?: {
  borough?: string;
  access?: string;
  q?: string;
  park?: string;
  sort?: string;
  page?: number;
}) => {
  if (!params) return NYC_TENNIS_GUIDE_PARKS_PAGE;
  const search = new URLSearchParams();
  if (params.borough) search.set('borough', params.borough);
  if (params.access) search.set('access', params.access);
  if (params.q) search.set('q', params.q);
  if (params.park) search.set('park', params.park);
  if (params.sort) search.set('sort', params.sort);
  if (params.page && params.page > 1) search.set('page', String(params.page));
  const query = search.toString();
  return query ? `${NYC_TENNIS_GUIDE_PARKS_PAGE}?${query}` : NYC_TENNIS_GUIDE_PARKS_PAGE;
};
export const PRIVACY_PAGE = '/privacy';
export const TERMS_PAGE = '/terms';
export const TOURNAMENT_CREATE_PAGE = '/tournaments/create';
export const TOURNAMENT_CREATE_EXTERNAL_PAGE = '/tournaments/create-external';
export const TOURNAMENT_QUICK_CREATE_PAGE = '/tournaments/quick';
export const TOURNAMENT_QUICK_CREATE_FLEXIBLE_PAGE = '/tournaments/quick/flexible';
// VAIR entry points into quick-create. The `vairFlow` param skips the generic
// "Is this a VAIR event?" step and is re-checked against the creator's VAIR role
// on arrival, so every VAIR entry button must link through one of these.
export const VAIR_RATING_SESSION_CREATE_PAGE = `${TOURNAMENT_QUICK_CREATE_PAGE}?vairFlow=rating-session`;
export const VAIR_INTEGRATED_EVENT_CREATE_PAGE = `${TOURNAMENT_QUICK_CREATE_PAGE}?vairFlow=integrated-event`;
export const VAIR_SIGNIN_PAGE = '/auth/vair';
/** VAIR's partner landing page — the only auth option it offers is "Continue with VAIR". */
export const VAIR_CONNECT_PAGE = '/vair/connect';
export const TOURNAMENTS_PAGE = '/tournaments';
export const COURT_FINDER_PAGE = '/courts';
export const CLINIC_FINDER_PAGE = '/clinics';
export const COACH_MARKETPLACE_PAGE = '/lessons';
export const CLINIC_MARKETPLACE_PAGE = '/clinics';
export const OPEN_PLAY_MARKETPLACE_PAGE = '/open-play';
export const LEAGUE_MARKETPLACE_PAGE = '/leagues';
export const SOCIAL_MARKETPLACE_PAGE = '/socials';
export const FIND_A_PRO_PPR_PAGE = '/find-a-pro-ppr';
export const FIND_A_PRO_PTR_PAGE = '/find-a-pro-ptr';
export const PICKLEBALL_RATING_CONVERTER_PAGE = '/pickleball-rating-converter';
export const TENNIS_RATING_CONVERTER_PAGE = '/tennis-rating-converter';
export const PADEL_RATING_CONVERTER_PAGE = '/padel-rating-converter';
export const PICKLEBALL_ROUND_ROBIN_GENERATOR_PAGE = '/pickleball-round-robin-generator';
export const PICKLEBALL_BRACKET_GENERATOR_PAGE = '/pickleball-bracket-generator';
export const COACH_PAGE = '/coach';
export const CITY_PAGE = '/city';
export const COUNTRY_PAGE = '/country';
export const COUNTRY_SUBDIVISION_PAGE = '/region';
export const COURT_PAGE = '/court';
export const EVENT_PAGE = '/event';
export const AUTH_SELECT_PAGE = '/auth';
export const HOSTED_TOURNAMENTS_PAGE = '/tournaments-hosted';
export const FORGOT_PASSWORD_PAGE = '/forgot-password';
export const LOGOUT_PAGE = '/logout';
export const LOGIN_PAGE = '/login';
export const SIGNUP_PAGE = '/signup';
export const SIGNUP_CODE_PAGE = '/signup-code';
export const CHAMPION_WAITLIST_PAGE = '/champion-waitlist';
export const CHAMPION_WAITLIST_SUCCESS_PAGE = '/champion-waitlist-success';
export const SIGNUP_COACH_PAGE = '/signup/coach';
export const SIGNUP_PLAYER_PAGE = '/signup/player';
export const ONBOARDING_ROOT = '/onboard';
export const ONBOARDING_COACH = `${ONBOARDING_ROOT}/coach`;
export const ONBOARDING_COACH_LOCATION_PAGE = `${ONBOARDING_COACH}/location`;
export const ONBOARDING_COACH_QUALIFICATIONS_PAGE = `${ONBOARDING_COACH}/qualifications`;
export const ONBOARDING_COACH_BIO_PAGE = `${ONBOARDING_COACH}/bio`;
export const ONBOARDING_COACH_LEGAL_PAGE = `${ONBOARDING_COACH}/legal`;
export const ONBOARDING_PLAYER = `${ONBOARDING_ROOT}/player`;
export const ONBOARDING_PLAYER_BIO_PAGE = `${ONBOARDING_PLAYER}/bio`;
export const ONBOARDING_PLAYER_LOCATION_PAGE = `${ONBOARDING_PLAYER}/location`;
export const ONBOARDING_PLAYER_FOLLOW_PAGE = `${ONBOARDING_PLAYER}/follow`;
export const ONBOARDING_PLAYER_LEGAL_PAGE = `${ONBOARDING_PLAYER}/legal`;
export const ONBOARDING_PLAYER_SKILL_LEVEL_PAGE = `${ONBOARDING_PLAYER}/skill`;
export const ONBOARDING_COMPLETE_PAGE = `${ONBOARDING_ROOT}/complete`;
export const PERSONAL_ROUTES_ROOT = '/my';
export const TEMPLATES_PAGE = `${PERSONAL_ROUTES_ROOT}/templates`; // OR SHOULD THIS JUST BE /templates?
export const QR_SHARE_PAGE = `${PERSONAL_ROUTES_ROOT}/share`; // OR SHOULD THIS JUST BE /share?
export const QR_SHARE_PROFILE_PAGE = `${PERSONAL_ROUTES_ROOT}/share-profile`;

export const EXPLORE_ALL_MARKETPLACES = `${EXPLORE_PAGE}/all`;
export const MY_CALENDAR_PAGE = `${PERSONAL_ROUTES_ROOT}/calendar`; // OR SHOULD THIS JUST BE /calendar or /lessons?
export const MY_LESSONS_PAGE = `${PERSONAL_ROUTES_ROOT}/lessons`; // OR SHOULD THIS JUST BE /calendar or /lessons?
export const MY_PLAYERS_PAGE = `${PERSONAL_ROUTES_ROOT}/followers`; // OR SHOULD THIS JUST BE /calendar or /lessons?
export const MY_PROFILE_PAGE = `${PERSONAL_ROUTES_ROOT}/profile`; // OR SHOULD THIS JUST BE /calendar or /lessons?
export const MY_COACHES_PAGE = `${PERSONAL_ROUTES_ROOT}/coaches`;
export const MY_SETTINGS_PAGE = `${PERSONAL_ROUTES_ROOT}/settings`;
export const MY_PERSONAL_INFO_PAGE = `${PERSONAL_ROUTES_ROOT}/personal-details`;
export const MY_PAYMENT_METHODS_PAGE = `${PERSONAL_ROUTES_ROOT}/payment-methods`;
export const MY_COACH_PROFILE_PAGE = `${PERSONAL_ROUTES_ROOT}/coach-profile`;
export const MY_COACH_SETTINGS_PAGE = `${PERSONAL_ROUTES_ROOT}/coach-settings`;
export const MY_COACH_PLAYERS_PAGE = `${PERSONAL_ROUTES_ROOT}/players`;
export const QUICK_ONBOARD_QUERY_PARAM = 'quickOnboard';
export const REVIEW_COACH_QUERY_PARAM = 'review';
export const SUBSCRIPTION_SUCCESS_QUERY_PARAM = 'subscriptionSuccess';
export const COACH_HOME_WITH_QUICK_ONBOARD_PAGE = `${ROOT_PAGE}?${QUICK_ONBOARD_QUERY_PARAM}=true`;
export const MY_SCHEDULE_PAGE = `${PERSONAL_ROUTES_ROOT}/schedule`;
export const MY_YOU_PAGE = `/you`;
export const MY_TRAINING_LOGS_PAGE = `${MY_YOU_PAGE}/training-log`;
export const CONNECTIONS_PAGE = `${MY_YOU_PAGE}/connections`;
export const SAVED_FAVORITES_PAGE = `${MY_YOU_PAGE}/favorites`;
export const SKILLS_FEEDBACK_PAGE = `${MY_YOU_PAGE}/skills`;
export const SESSION_HISTORY_PAGE = `${MY_YOU_PAGE}/session-history`;
export const MY_INVITE_PAGE = `${PERSONAL_ROUTES_ROOT}/invite`;
export const MY_PERSONAL_DETAILS_PAGE = `${PERSONAL_ROUTES_ROOT}/personal-details`;
export const MY_GROUPS_PAGE = `/groups`;
export const GROUPS_MARKETPLACE_PAGE = '/groups';
export const DEFAULT_TEAM_BOUNCE_LINK = 'community@bounce.game';
export const TOURNAMENTS_LANDING_PAGE = '/tournament-software';
export const MY_PAYMENT_DETAILS_PAGE = `${PERSONAL_ROUTES_ROOT}/payment-details`;
export const MY_NOTIFICATION_SETTINGS_PAGE = `${PERSONAL_ROUTES_ROOT}/notification-settings`;
export const MY_CHANGE_PASSWORD_PAGE = `${PERSONAL_ROUTES_ROOT}/change-password`;
export const MY_GROUPS = `${PERSONAL_ROUTES_ROOT}/groups`; // IS THIS RIGHT? Current could just show their primary group but eventually list of their groups
export const MY_COMPETE = `${PERSONAL_ROUTES_ROOT}/compete`; // IS THIS RIGHT? Current could just show their primary group but eventually list of their groups
export const MY_ACTIVITY_PAGE = `${PERSONAL_ROUTES_ROOT}/activity`;
export const MY_TOURNAMENTS_PAGE = `${PERSONAL_ROUTES_ROOT}/tournaments`;
export const getMyTournamentDashboardUrl = (eventId: string) => `${MY_TOURNAMENTS_PAGE}/${eventId}`;
export const getEventGroupPlayerManagementPageUrl = (eventId: string, groupId: string) =>
  `${MY_TOURNAMENTS_PAGE}/${eventId}/groups/${groupId}/player-management`;
export const getMyTournamentCheckInUrl = (eventId: string) =>
  `${MY_TOURNAMENTS_PAGE}/${eventId}/check-in`;
export const getEventGroupCheckInPageUrl = (eventId: string, groupId: string) =>
  `/tournaments/${eventId}/gameday/${groupId}`;
export const getEventGroupEventPageUrl = (eventId: string, groupId: string, tab?: string) =>
  tab
    ? `/tournaments/${eventId}/gameday/${groupId}?tab=${tab}`
    : `/tournaments/${eventId}/gameday/${groupId}`;
export const CONTACT_US = `/contact`;
export const MY_NOTIFICATIONS_PAGE = `${PERSONAL_ROUTES_ROOT}/notifications`;
export const STRIPE_ONBOARD_REFRESH = `${PERSONAL_ROUTES_ROOT}/stripe/connect/refresh`;
export const STRIPE_CONNECT_PAGE = `${PERSONAL_ROUTES_ROOT}/stripe-connect`;
export const COACHES_LANDING_PAGE = '/coaching-software';
export const CLUB_LANDING_PAGE = '/club';
export const ADVERTISE_PAGE = '/advertise';
export const COACHES_ON_COURT_PAGE = `${COACHES_LANDING_PAGE}/on-court`;
export const COACHES_REMOTE_PAGE = `${COACHES_LANDING_PAGE}/remote`;
export const COACHES_PRICING_PAGE = `${COACHES_LANDING_PAGE}/pricing`;
export const COACHES_BOUNCE_FOR_PAGE = `${COACHES_LANDING_PAGE}/bounce-for`;
export const COACH_HOME_PAGE = `/`;
export const NEW_LESSON_PAGE = '/new-lesson';
export const NEW_PLAY_SESSION_PAGE = '/new-play';
export const PREVIEW_PAGE = '/preview';
export const LESSON_PUBLISHED_PAGE = '/published';
export const PLAY_SESSION_PUBLISHED_PAGE = '/published';
export const PLAY_SESSION_EDIT_EXISTING_PAGE = '/edit';
export const PLAY_SESSION_JOIN_PAGE = '/join';
export const PLAY_SESSION_JOIN_SUCCESS_PAGE = `${PLAY_SESSION_JOIN_PAGE}/success`;
export const LESSON_REPEAT_PAGE = '/repeat';
export const LESSON_JOIN_PAGE = '/join';
export const LESSON_JOIN_SUCCESS_PAGE = `${LESSON_JOIN_PAGE}/success`;
export const LESSON_JOIN_PAYMENT_FULFILLMENT_CHANNEL_PAGE = `${LESSON_JOIN_PAGE}/method`;
export const HOW_LESSON_WORKS_FOR_PLAYER_PAGE = '/how-lessons-work-for-player';
export const MY_CLUB_PAGE = `${PERSONAL_ROUTES_ROOT}/club`;
export const MY_CLUB_EDIT_PAGE = `${MY_CLUB_PAGE}/edit`;
export const MY_CLUB_DASHBOARD_PAGE = `${MY_CLUB_PAGE}/dashboard`;
export const MY_CLUB_COACHES_PAGE = `${MY_CLUB_PAGE}/coaches`;
export const MY_CLUB_LESSONS_PAGE = `${MY_CLUB_PAGE}/lessons`;
export const MY_CLUB_SETTINGS_PAGE = `${MY_CLUB_PAGE}/settings`;
export const MY_CLUB_SETTINGS_BOOKING_PAGE = `${MY_CLUB_SETTINGS_PAGE}/booking`;
export const MY_CLUB_SETTINGS_CONNECTION_PAGE = `${MY_CLUB_SETTINGS_BOOKING_PAGE}/connection`;
export const MY_CLUB_SETTINGS_COURT_RULES_PAGE = `${MY_CLUB_SETTINGS_BOOKING_PAGE}/court-rules`;
export const MY_CLUB_SETTINGS_PROGRAMMING_RULES_PAGE = `${MY_CLUB_SETTINGS_BOOKING_PAGE}/programming-rules`;
export const MY_CLUB_ONBOARDING_PAGE = `${MY_CLUB_PAGE}/onboarding`;
export const MY_CLUB_ONBOARDING_BOOKING_MODELS_PAGE = `${MY_CLUB_ONBOARDING_PAGE}/booking-models`;
export const MY_CLUB_ONBOARDING_COURT_RULES_PAGE = `${MY_CLUB_ONBOARDING_PAGE}/court-rules`;
export const MY_CLUB_ONBOARDING_PROGRAMMING_RULES_PAGE = `${MY_CLUB_ONBOARDING_PAGE}/programming-rules`;
export const MY_CLUB_ONBOARDING_REVIEW_ACTIVATE_PAGE = `${MY_CLUB_ONBOARDING_PAGE}/review-activate`;
export const MY_CLUB_ONBOARDING_ACTIVATION_COMPLETE_PAGE = `${MY_CLUB_ONBOARDING_PAGE}/complete`;
export const PROGRAMMING_PAGE = `${PERSONAL_ROUTES_ROOT}/club/programming`;
export const CREATE_PROGRAM_PAGE = `${PROGRAMMING_PAGE}/create`;
/** Create-program flow, optionally with the program type prefilled (`?type=`). */
export const getCreateProgramUrl = (programType?: ProgrammingTypeValue) =>
  programType ? `${CREATE_PROGRAM_PAGE}?type=${programType}` : CREATE_PROGRAM_PAGE;
export const getProgrammingEditPageUrl = (programId: string) =>
  `${PROGRAMMING_PAGE}/${programId}/edit`;
export const getProgrammingDetailPageUrl = (programId: string) =>
  `${PROGRAMMING_PAGE}/${programId}`;

export const getNewLessonEditPageUrl = (lessonId: string) => `${NEW_LESSON_PAGE}/${lessonId}`;
export const getNewLessonPreviewPageUrl = (lessonId: string) =>
  `${NEW_LESSON_PAGE}/${lessonId}${PREVIEW_PAGE}`;
export const getLessonPublishedPageUrl = (lessonId: string) =>
  `${NEW_LESSON_PAGE}/${lessonId}${LESSON_PUBLISHED_PAGE}`;
export const getLessonRepeatUrl = (lessonId: string) =>
  `${NEW_LESSON_PAGE}/${lessonId}${LESSON_REPEAT_PAGE}`;
export const LESSONS_PAGE = '/lessons';
export const REMOTE_COACHES_PAGE = '/remote';
export const LESSON_EDIT_EXISTING_PAGE = '/edit';
export const getLessonPageUrl = (lessonId: string) => `${LESSONS_PAGE}/${lessonId}`;
export const getExistingLessonEditPageUrl = (lessonId: string) =>
  `${LESSONS_PAGE}/${lessonId}${LESSON_EDIT_EXISTING_PAGE}`;
export const getLessonJoinPageUrl = (lessonId: string) =>
  `${LESSONS_PAGE}/${lessonId}${LESSON_JOIN_PAGE}`;
export const getLessonJoinSuccessPageUrl = (lessonId: string) =>
  `${LESSONS_PAGE}/${lessonId}${LESSON_JOIN_SUCCESS_PAGE}`;
export const getLessonPaymentFulfillmentChannelPageUrl = (lessonId: string) =>
  `${LESSONS_PAGE}/${lessonId}${LESSON_JOIN_PAYMENT_FULFILLMENT_CHANNEL_PAGE}`;

export const getNewPlaySessionPreviewPageUrl = (playSessionId: string) =>
  `${NEW_PLAY_SESSION_PAGE}/${playSessionId}${PREVIEW_PAGE}`;
export const getNewPlaySessionEditPageUrl = (playSessionId: string) =>
  `${NEW_PLAY_SESSION_PAGE}/${playSessionId}`;
export const getExistingPlaySessionEditPageUrl = (playSessionId: string) =>
  `${PLAY_PAGE}/${playSessionId}${PLAY_SESSION_EDIT_EXISTING_PAGE}`;
export const getPlaySessionPageUrl = (playSessionId: string) => `${PLAY_PAGE}/${playSessionId}`;
export const getPlaySessionPublishedPageUrl = (playSessionId: string) =>
  `${NEW_PLAY_SESSION_PAGE}/${playSessionId}${PLAY_SESSION_PUBLISHED_PAGE}`;
export const getPlaySessionJoinSuccessPageUrl = (playSessionId: string) =>
  `${PLAY_PAGE}/${playSessionId}${PLAY_SESSION_JOIN_SUCCESS_PAGE}`;
export const getMyGroupMembersPageUrl = (groupId: string) => `${MY_GROUPS}/${groupId}/members`;
export const getMyGroupVenuesPageUrl = (groupId: string) => `${MY_GROUPS}/${groupId}/courts`;
export const getMyGroupThreadPageUrl = ({
  groupId,
  threadId,
}: {
  groupId: string;
  threadId: string;
}) => `${MY_GROUPS}/${groupId}/threads/${threadId}`;

export const getProfilePageUrl = (username?: string | null) => {
  return username ? `/${username}` : '';
};
export const getEditQuickCreatePageUrl = (eventId: string) =>
  `${TOURNAMENT_CREATE_PAGE}?eventId=${eventId}`;

export const getArticlePageUrl = (slug: string) => `${BLOG_PAGE}/${slug}`;
export const getBlogCategoryPageUrl = (slug: string) => `${BLOG_PAGE}/category/${slug}`;

export const getCountryIndexPageUrl = (slug: string) => `${COUNTRY_PAGE}/${slug}`;
export const getCountrySubdivisionIndexPageUrl = (slug: string) =>
  `${COUNTRY_SUBDIVISION_PAGE}/${slug}`;
export const getCityCourtsPageUrl = (slug: string) => `${CITY_PAGE}/${slug}/courts`;
export const getCityAllClinicUrl = (idOrSlug: string) => `${getCityIndexPageUrl(idOrSlug)}/clinics`;
export const getCourtPageUrl = (slug: string) => `${COURT_PAGE}/${slug}`;
export const getCoachPageUrl = (slug: string) => `${COACH_PAGE}/${slug}`;

export const getEventUrl = (idOrSlug: string) => `${EVENT_PAGE}/${idOrSlug}`;
export const getEventGamedayUrl = (idOrSlug: string) => `${getEventUrl(idOrSlug)}/gameday`;
// New Gameday
export const getGamedayUrl = (idOrSlug: string) => `${getEventUrl(idOrSlug)}/gameday/draw`;
export const getCreateDrawUrl = (idOrSlug: string) => `${getEventUrl(idOrSlug)}/createdraw`;
export const getCancelTournamentUrl = (idOrSlug: string) =>
  `${getEventUrl(idOrSlug)}/canceltournament`;

export const getCourtVenueUrl = (idOrSlug: string) => `${COURT_PAGE}/${idOrSlug}`;
export const getCityIndexPageUrl = (slug: string) => `/city/${slug}`;
export const getCityAllCourtVenueUrl = (idOrSlug: string) =>
  `${getCityIndexPageUrl(idOrSlug)}/courts`;
export const getCityAllLessonUrl = (idOrSlug: string) => `${getCityIndexPageUrl(idOrSlug)}/lessons`;

export const FREE_COURT_SLUG = 'free';
export const INDOOR_COURT_SLUG = 'indoor';
export const OUTDOOR_COURT_SLUG = 'outdoor';

export const getCityAllCourtTagVenueUrl = (idOrSlug: string, tagSlug: string) =>
  `${getCityIndexPageUrl(idOrSlug)}/courts/tag/${tagSlug}`;

export const getCityCourtsPageUrlWithSport = (slug: string, sport?: string) =>
  `${sport ? `/${sport}` : ''}${getCityCourtsPageUrl(slug)}`;

export const getCityAllClinicUrlWithSport = (idOrSlug: string, sport?: string) =>
  `${sport ? `/${sport}` : ''}${getCityAllClinicUrl(idOrSlug)}`;

export const getCityAllLessonUrlWithSport = (idOrSlug: string, sport?: string) =>
  `${sport ? `/${sport}` : ''}${getCityAllLessonUrl(idOrSlug)}`;

export const getCityAllCourtTagVenueUrlWithSport = (
  idOrSlug: string,
  tagSlug: string,
  sport?: string,
) => `${sport ? `/${sport}` : ''}${getCityAllCourtTagVenueUrl(idOrSlug, tagSlug)}`;

export const getCountryIndexPageUrlWithSport = (slug: string, sport?: string) =>
  `${sport ? `/${sport}` : ''}${getCountryIndexPageUrl(slug)}`;

export const getCityIndexPageUrlWithSport = (slug: string, sport?: string) =>
  `${sport ? `/${sport}` : ''}${getCityIndexPageUrl(slug)}`;

export const getCourtPageUrlWithSport = (slug: string, sport?: string) =>
  `${sport ? `/${sport}` : ''}${getCourtPageUrl(slug)}`;

export const getCityAllCourtVenueUrlWithSport = (idOrSlug: string, sport?: string) =>
  `${sport ? `/${sport}` : ''}${getCityAllCourtVenueUrl(idOrSlug)}`;

export const getCountrySubdivisionIndexPageUrlWithSport = (slug: string, sport?: string) =>
  `${sport ? `/${sport}` : ''}${getCountrySubdivisionIndexPageUrl(slug)}`;

// export const getAllTournamentsVenueUrl = (idOrSlug: string) => `/event/${idOrSlug}`; // We dont have all tournaments for specific city. Confirmed with Trey
export const getQuickEventPageUrl = (idOrSlug: string) => `/quick-create/${idOrSlug}`;
export const CITY_AUSTIN = getCityCourtsPageUrl('austin-texas-us');
export const CITY_LASVEGAS = getCityCourtsPageUrl('las-vegas-nevada-us');
export const CITY_SEATTLE = getCityCourtsPageUrl('seattle-washington-us');
export const CITY_CHICAGO = getCityCourtsPageUrl('chicago-illinois-us');
export const CITY_NEWYORK = getCityCourtsPageUrl('new-york-new-york-us');
export const CITY_HOUSTON = getCityCourtsPageUrl('houston-texas-us');
export const CITY_MIAMI = getCityCourtsPageUrl('miami-florida-us');
export const CITY_DALLAS = getCityCourtsPageUrl('dallas-texas-us');
export const CITY_SANANTONIO = getCityCourtsPageUrl('san-antonio-texas-us');
export const CITY_NAPLES = getCityCourtsPageUrl('naples-florida-us');
export const CITY_JACKSONVILLE = getCityCourtsPageUrl('jacksonville-florida-us');
export const CITY_ORLANDO = getCityCourtsPageUrl('orlando-florida-us');
export const CITY_TAMPA = getCityCourtsPageUrl('tampa-florida-us');
export const CITY_STPETERSBURG = getCityCourtsPageUrl('st-petersburg-florida-us');
export const CITY_SARASOTA = getCityCourtsPageUrl('sarasota-florida-us');
export const COUNTRY_USA_PAGE = getCountryIndexPageUrl('us');
export const SERVICES_PAGE = '/services';
export const CHATS_PAGE = `${PERSONAL_ROUTES_ROOT}/chats`;
/** The player-side chat. A separate experience from the coach's CHATS_PAGE. */
export const PLAYER_CHATS_PAGE = '/chats';
/** One accepted thread, keyed by conversation. */
export const getPlayerChatPageUrl = (conversationId: string) =>
  `${PLAYER_CHATS_PAGE}/${conversationId}`;
/** A still-pending play request. It has no conversation row yet (one is only created
 *  on accept), so it gets its own route rather than sharing the thread URL. */
export const getPlayerChatRequestPageUrl = (playRequestId: string) =>
  `${PLAYER_CHATS_PAGE}/request/${playRequestId}`;
export const getGroupPageUrl = (groupId: string) => `${MY_GROUPS_PAGE}/${groupId}`;
export const getVideoAnalysisPageUrl = (videoId: string) => `/video-analysis/${videoId}`;
export const getCoachPremiumPage = (username: string) => `${getCoachPageUrl(username)}/content`;
export const getGroupDiscussionPageUrl = (groupSlug: string) =>
  `${GROUPS_MARKETPLACE_PAGE}/${groupSlug}/threads`;
export const getThreadDetailPageUrl = (groupSlug: string, threadId: string) =>
  `${GROUPS_MARKETPLACE_PAGE}/${groupSlug}/threads/${threadId}`;

export const COACH_ONBOARDING_STEPS = [
  { name: 'Where you coach', url: ONBOARDING_COACH_LOCATION_PAGE },
  { name: 'Share your qualifications', url: ONBOARDING_COACH_QUALIFICATIONS_PAGE },
  { name: 'Tell us a little about yourself', url: ONBOARDING_COACH_BIO_PAGE },
  { name: 'Your data protection', url: ONBOARDING_COACH_LEGAL_PAGE },
];
export const PLAYER_ONBOARDING_STEPS = [
  { name: 'Tell us a little about yourself', url: ONBOARDING_PLAYER_BIO_PAGE },
  { name: 'Your skill level', url: ONBOARDING_PLAYER_SKILL_LEVEL_PAGE },
  // {name: '', url: ONBOARDING_PLAYER_LOCATION_PAGE},
  { name: 'Your data protection', url: ONBOARDING_PLAYER_LEGAL_PAGE },
  // { name: 'Follow your coaches', url: ONBOARDING_PLAYER_FOLLOW_PAGE },
];

export const COACH_TABS = [
  { Icon: Home, text: 'Home', href: COACH_HOME_PAGE },
  { Icon: Calendar, text: 'Schedule', href: MY_SCHEDULE_PAGE },
  { Icon: PlusWithoutBG, text: 'actions', href: '' },
  { Icon: Players, text: 'Clients', href: MY_PLAYERS_PAGE },
  { Icon: UserCircle, text: 'You', href: '' },
];

export const PLAYER_TABS = [
  { Icon: Trophy, text: 'Compete', href: TOURNAMENT_MARKET_PLACE },
  { Icon: Court, text: 'Courts', href: COURT_FINDER_PAGE },
  { Icon: RacketBall, text: 'Play', href: PLAY_PAGE },
  // { Icon: Group, text: 'Group', href: MY_GROUPS },
  { Icon: NotificationIndicator, text: 'Notifications', href: MY_NOTIFICATIONS_PAGE },
  // { Icon: Home, text: 'Home', href: HOME_PAGE },
  // { Icon: RacketBall, text: 'Train', href: TRAIN_PAGE },
  // { Icon: UserCircleIcon, text: 'Profile', href: MY_PROFILE_PAGE },
];

export const PLAYER_WITH_COACH_TABS = [
  ...PLAYER_TABS.slice(0, Math.floor(PLAYER_TABS.length / 2)),
  { Icon: RacketTwo, text: 'Train', href: TRAIN_PAGE },
  ...PLAYER_TABS.slice(Math.floor(PLAYER_TABS.length / 2)),
];

export const COACH_SIDEBAR = [
  { href: HOME_PAGE, Icon: CalendarDaysIcon, text: 'Lessons' },
  { href: MY_PLAYERS_PAGE, Icon: UserGroupIcon, text: 'Players' },
  // { Icon: RacketBall, text: 'Play', href: PLAY_PAGE },
  { href: QR_SHARE_PAGE, Icon: QrCodeIcon, text: 'Share' },
  { href: MY_NOTIFICATIONS_PAGE, Icon: NotificationIndicator, text: 'Notifications' },
  // { href: MY_PROFILE_PAGE, Icon: UserCircleIcon, text: 'Profile' },
];

export const PLAYER_SIDEBAR = [
  { Icon: Trophy, text: 'Compete', href: TOURNAMENT_MARKET_PLACE },
  { Icon: Court, text: 'Court Finder', href: COURT_FINDER_PAGE },
  { Icon: RacketBall, text: 'Play', href: PLAY_PAGE },
  { Icon: NotificationIndicator, text: 'Notifications', href: MY_NOTIFICATIONS_PAGE },
  // { Icon: QrCodeIcon, text: 'Share', href: QR_SHARE_PAGE },
  // { Icon: Home, text: 'Home', href: HOME_PAGE },
  // { Icon: RacketBall, text: 'Train', href: TRAIN_PAGE },
  // { Icon: UserCircleIcon, text: 'Profile', href: MY_PROFILE_PAGE },
];

export const PLAYER_WITH_COACH_SIDEBAR = [
  ...PLAYER_SIDEBAR.slice(0, Math.floor(PLAYER_SIDEBAR.length / 2)),
  { Icon: RacketTwo, text: 'Train', href: TRAIN_PAGE },
  ...PLAYER_SIDEBAR.slice(Math.floor(PLAYER_SIDEBAR.length / 2)),
];

export const ACTIVE_COUNTRIES = [
  {
    id: 'USA',
    slug: 'us',
    displayNameOverride: 'The United States',
    hasSubdivisionImages: true,
  },
  {
    id: 'CAN',
    slug: 'ca',
  },
  /**
   * @note Mexico is failing because we haven't activated subdivisions
   */
  {
    id: 'MEX',
    slug: 'mx',
  },
];

// quatro pages

export const QUATRO_HOME = '/quatro';
export const QUATRO_CLINICS = '/quatro/clinics';
export const QUATRO_GETAWAYS = '/quatro/getaways';
export const QUATRO_COACHES = '/quatro/coaches';
export const QUATRO_FOUNDER = '/quatro/about/founder';
export const QUATRO_ABOUT = '/quatro/about';
export const QUATRO_CONTACT = '/quatro/contact';
export const getQuatroGetawayDetailPage = (id: string) => `/getaways/${id}`;

// new coach home bounce for pages

export const CardioTennis = 'cardio-tennis';
export const ContentCreators = 'content-creators';
export const CoachingAssociations = 'coaching-associations';
export const EventOrganizers = 'event-organizers';
export const HighShool = 'high-school';
export const Parents = 'parents';
export const RacquetSports = 'racket-sports';

/**
 * Appends tracking parameters to an external booking URL
 * @param externalBookingUrl - The original external booking URL
 * @param lessonId - The lesson ID for tracking
 * @returns The URL with tracking parameters appended
 */
export const addTrackingToExternalBookingUrl = (
  externalBookingUrl: string,
  lessonId: string,
): string => {
  if (!externalBookingUrl?.trim()) {
    return externalBookingUrl;
  }

  const trackingParams = `ref=bounce&utm_source=bounce&bounce_id=${lessonId}`;
  const separator = externalBookingUrl.includes('?') ? '&' : '?';

  return `${externalBookingUrl}${separator}${trackingParams}`;
};

/**
 * Appends tracking parameters to a coach's external booking URL
 * @param externalBookingUrl - The original external booking URL
 * @param coachId - The coach ID for tracking
 * @returns The URL with tracking parameters appended
 */
export const addTrackingToCoachExternalBookingUrl = (
  externalBookingUrl: string,
  coachId: string,
): string => {
  if (!externalBookingUrl?.trim()) {
    return externalBookingUrl;
  }

  const trackingParams = `ref=bounce&utm_source=bounce&bounce_coach_id=${coachId}`;
  const separator = externalBookingUrl.includes('?') ? '&' : '?';

  return formatUrl(`${externalBookingUrl}${separator}${trackingParams}`);
};

export const getSkillDetailPageUrl = (skillId: string) => `/skills/${skillId}`;
export const LEARNING_PROGRAMS_PAGE = '/learning-programs';
export const getLearningProgramPageUrl = (programId: string) =>
  `${LEARNING_PROGRAMS_PAGE}/${programId}`;

export const FIND_PAGE = '/find';
export const FIND_ONBOARDING_PAGE = '/find/onboarding';
export const NEW_GAME_PAGE = '/new-game';
export const NEW_AVAILABILITY_PAGE = '/new-availability';
export const NEW_GAME_INVITE_QUERY_PARAM = 'invite';
/** A connection's "Invite to play" opens the listing flow with them pre-invited. */
export const getNewGamePageUrl = (invitePlayerId?: string) =>
  invitePlayerId
    ? `${NEW_GAME_PAGE}?${NEW_GAME_INVITE_QUERY_PARAM}=${encodeURIComponent(invitePlayerId)}`
    : NEW_GAME_PAGE;
export const getEditGamePageUrl = (eventId: string) => `/edit-game/${eventId}`;
export const getEditAvailabilityPageUrl = (availabilityId: string) =>
  `/edit-availability/${availabilityId}`;
export const FIND_TAB_QUERY_PARAM = 'tab';
export const getFindPageUrl = (tab: FindTab) => `${FIND_PAGE}?${FIND_TAB_QUERY_PARAM}=${tab}`;

/** Opens /find with one game's detail sheet already up — what a notification links to. */
export const FIND_GAME_QUERY_PARAM = 'game';
export const getFindGameUrl = (gameId: string) =>
  `${FIND_PAGE}?${FIND_TAB_QUERY_PARAM}=${
    FindTab.Games
  }&${FIND_GAME_QUERY_PARAM}=${encodeURIComponent(gameId)}`;
